import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../../../components/Navbar";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import Stack from "@mui/material/Stack";
import { addClientManually, deleteClient, getAllClients } from "./Helper";
import { hasAddonProductAccess, updateUserProductAccess } from "../../../utils/common/api";
import { IconButton, Switch } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LoginIcon from "@mui/icons-material/Login";
import { Button, TablePagination } from "@mui/material";
import { declineClient } from "../../PendingApproval/Client/Helper";
import { useDebounce, useQueryParams } from "../../../hooks";
import { DeactivateUserModal } from "../../../components/common";
import countryData from 'country-calling-code';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import SignupForm from "../../../components/SignupForm";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ClientUserList = () => {
  const context = useOutletContext();
  const [clients, setClients] = useState([]);
  const { getParam, getAllQueryParamString, setMultiParam, removeParam } = useQueryParams();
  const [userPermissions, setUserPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const currentPage = getParam("page");
  const currentLimit = getParam("pageSize");
  const [clientsPage, setClientsPage] = useState(parseInt(currentPage) || 0);
  const [clientsLimit, setClientsLimit] = useState(parseInt(currentLimit) || 10);
  const [clientsTotal, setClientsTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [toggle, setToggle] = useState(true);
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false);
  const [clientInfo, setClientInfo] = useState({id: ""});
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const debounceSearch = useDebounce(search, 1000);
  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [updatingAccessId, setUpdatingAccessId] = useState(null);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
    setSuccessMessage('');
    setErrorMessage('');
  };  

  const handleChangePage = (event, newPage) => {
    fetchData(newPage, clientsLimit);
    setClientsPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setClientsLimit(parseInt(event.target.value, 10));
    setClientsPage(0);
    fetchData(0, parseInt(event.target.value, 10));
  };

  const fetchData = (page, limit) => {
    getAllClients(page, limit, '', fromDate || null, toDate || null)
      .then((data) => {
        setClients(data.data.clients.rows);
        setClientsTotal(data.data.clients.totalItems);
      })
      .catch((err) => console.log(err));
  };

  const navigate = useNavigate();
  useEffect(() => {
    context(["User Manager", "Client"]);
    getAllClients(clientsPage, clientsLimit, debounceSearch, fromDate || null, toDate || null)
      .then((data) => {
        setClients(data.data.clients.rows);
        setClientsTotal(data.data.clients.totalItems);
      })
      .catch((err) => console.log(err));
  }, [debounceSearch, fromDate, toDate, clientsPage, clientsLimit]);

  useEffect(() => {
    const cmsPermissions = localStorage.getItem('CMSUsersPermission');
    if (cmsPermissions) {
      setUserPermissions(JSON.parse(cmsPermissions));
      setIsAdmin(false);
    } else {
      setUserPermissions([]);
      setIsAdmin(true);
    }
  }, []);

  useEffect(() => {
    removeParam("user-name")
  }, [toggle]);

  useEffect(() => {
    setMultiParam({"page": clientsPage, "pageSize": clientsLimit});
  }, [clientsPage, clientsLimit])

  const viewClient = (e, id) => {
    e.preventDefault();
  };
  
  const removeClient = () => {
    deleteClient(clientInfo.id)
      .then(() => {
        fetchData(clientsPage, clientsLimit);
        setOpen(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleAddClient = async (formData) => {
    setIsCreatingClient(true);
  
    const payload = {
      email: formData.email,
      companyName: formData.companyName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      password: formData.password,
      phone: formData.phone,
      website: formData.website || "",
      countryCode: formData.countryCode,
      role: "CLIENT",
    };
  
    try {
      await addClientManually(payload);
      fetchData(clientsPage, clientsLimit);
      setSuccessMessage("Client created successfully!");
      setOpenSnackbar(true);
      setShowAddClientModal(false);
    } catch (error) {
      setErrorMessage('Error creating vendor. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setIsCreatingClient(false);
    }
  };
  const mappedClients = useMemo(() => {
    return clients.map((item) => ({
      ...item,
      // vendorId: <Link to={`vendor-list/${item?.id}`}>View Vendors List</Link>
    }))
  },[clients])

  const vmsAccessHandler = async (client, enabled) => {
    setUpdatingAccessId(client?.id);
    try {
      const { data } = await updateUserProductAccess(client?.id, enabled);
      const productAccess = data?.data?.productAccess;
      setClients((prev) =>
        prev.map((item) =>
          item?.id === client?.id
            ? { ...item, product_access: productAccess || item?.product_access }
            : item
        )
      );
      setSuccessMessage(data?.message || `VMS access ${enabled ? "enabled" : "disabled"} successfully`);
      setOpenSnackbar(true);
    } catch (error) {
      setErrorMessage("Error updating VMS access. Please try again.");
      setOpenSnackbar(true);
    } finally {
      setUpdatingAccessId(null);
    }
  };

  const handleNavigateUserInfo = (id, name) => {
    navigate(`client-info/${id}${getAllQueryParamString}&&user-name=${name}`)
  }

  const handleClearAll = () => {
    setFromDate("");
    setToDate("");
    setClientsPage(0);
  };

  return (
    <div className="p-5 shadow-md bg-white">
      {open && <DeactivateUserModal open={open} setOpen={setOpen} title={"Delete this Client"} deactivateUser={removeClient} />}
      <SignupForm
        open={showAddClientModal}
        onClose={() => setShowAddClientModal(false)}
        onSubmit={handleAddClient}
        title="Add New Client"
        submitButtonText="Create Client"
        isLoading={isCreatingClient}
      />
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-4 gap-3">
        <div className="flex flex-col">
          <h3 className="text-lg uppercase tracking-wider font-bold text-[#2EBAA3]">
            Clients
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
              Total:
            </span>
            <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
              {clientsTotal}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {(isAdmin || userPermissions.includes('user-manager')) && (
            <Button 
              variant="contained" 
              style={{ backgroundColor: "#2EBAA3", textTransform: 'none', color: "#fff", height: '38px' }}
              onClick={() => setShowAddClientModal(true)}
            >
              Add Client
            </Button>
          )}
          <input
            type="text"
            id="search"
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="px-3 py-1.5 form-control block text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out focus:outline-none"
            style={{ minWidth: '200px', height: '38px' }}
          />
          <div className="flex flex-col">
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
            <DatePicker
              selected={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                setFromDate(date ? date.toISOString().split("T")[0] : "");
                setClientsPage(0); // Reset pagination when date changes
              }}
              dateFormat="dd-MM-yyyy"
              placeholderText="dd/mm/yyyy"
              maxDate={new Date()}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-32"
              style={{ height: '38px' }}
            />
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium text-gray-700 mb-1">End Date</label>
            <DatePicker
              selected={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                setToDate(date ? date.toISOString().split("T")[0] : "");
                setClientsPage(0); // Reset pagination when date changes
              }}
              dateFormat="dd-MM-yyyy"
              minDate={fromDate ? new Date(fromDate) : null}
              maxDate={new Date()}
              placeholderText="dd/mm/yyyy"
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm w-32"
              style={{ height: '38px' }}
            />
          </div>
          <button
            onClick={handleClearAll}
            className="px-3 py-1 text-sm bg-[#2EBAA3] text-white rounded-lg shadow-md hover:bg-[#26a691] transition-colors duration-200"
            style={{ height: '38px' }}
          >
            Clear All
          </button>
        </div>
      </div>
      <br />
      <div class="overflow-x-auto relative">
        <table class="w-full text-sm text-left text-gray-500">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" class="py-3 px-6 uppercase">
                Sr.
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Name
              </th>
              {/* <th scope="col" class="py-3 px-6 uppercase">
                Vendors List
              </th> */}
              <th scope="col" class="py-3 px-6 uppercase">
                Date Created
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Username
              </th>
              <th scope="col" class="py-3 px-10 uppercase">
                Email ID
              </th>
              <th scope="col" class="py-3 px-10 uppercase">
                Country
              </th>
              <th scope="col" class="py-3 px-10 uppercase">
                Mobile Number
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Company
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Source
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Status
              </th>
            {(isAdmin || userPermissions.includes('user-manager')) && (
              <th scope="col" class="py-3 px-6 uppercase">
                VMS Access
              </th>
            )}
            {(isAdmin || userPermissions.includes('user-manager')) && (
              <th scope="col" class="py-3 px-6 uppercase text-center">
                Action
              </th>
            )}
            </tr>
          </thead>
          <tbody>
            {mappedClients.map((client, index) => {
              return (
                <tr class="bg-white border-b ">
                  <th
                    scope="row"
                    class="py-4 px-6 font-medium text-gray-900 whitespace-nowrap cursor-pointer"
                    // onClick={(e) => viewCustomer(e, customer._id)}
                    key={index}
                  >
                    {index + 1}
                  </th>
                  <td class="py-4 px-6 cursor-pointer hover:underline" onClick={()=> handleNavigateUserInfo(client?.id, `${client?.company}`)}>
                    {client?.firstName + " " + client?.lastName}
                  </td>
                  {/* <td class="py-4 px-6">{client?.vendorId}</td> */}
                  <td class="py-4 px-6">{client?.createdAt?.split("T")[0]}</td>
                  <td class="py-4 px-6">{client?.username}</td>
                  <td class="py-4 px-6">{client?.email}</td>
                  <td class="py-4 px-10">{client?.country ?  client?.country : countryData.find(c => c.countryCodes.includes(client.countryCode))?.country || '--'}</td>
                  <td class="py-4 px-2">{client?.countryCode
                    ? `${client.countryCode.startsWith('+') ? '' : '+'}${client.countryCode} ${client?.phone || ''}`
                    : client?.phone || ''}</td>
                  <td class="py-4 px-6">{client?.company}</td>
                  <td class="py-4 px-6">
                    {client?.source ? `${client?.source}` : '--'}
                    {(() => {
                      const parts = [
                        client?.utm_medium,
                        client?.utm_campaign,
                      ].filter(part => part?.trim());

                      return parts.length > 0 ? ` | ${parts.join(' | ')}` : <span className="italic text-gray-400 text-center tect-lg"></span>;
                    })()}
                    </td>
                  <td class="py-4 px-6">
                    <b>
                      {client?.isAccountApproved
                        ? "Active"
                        : "Approval Pending"}
                    </b>
                  </td>
                  {(isAdmin || userPermissions.includes('user-manager')) && (
                  <td class="py-4 px-6">
                    <Switch
                      checked={hasAddonProductAccess(client?.product_access)}
                      disabled={updatingAccessId === client?.id}
                      onChange={(e) => vmsAccessHandler(client, e.target.checked)}
                      color="primary"
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  </td>
                  )}
                  {(isAdmin || userPermissions.includes('user-manager')) && (
                  <td class="py-4 px-6">
                    <div className="grid gap-2 grid-cols-3">
                        <IconButton
                          aria-label="delete"
                          onClick={(e) => (e.preventDefault(), setOpen(true), setClientInfo({id: client?.id}))}
                        >
                          <DeleteIcon />
                        </IconButton>
                    </div>
                  </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={clientsTotal}
        page={clientsPage}
        onPageChange={handleChangePage}
        rowsPerPage={clientsLimit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert 
          onClose={handleCloseSnackbar} 
          severity={errorMessage ? "error" : "success"} 
          sx={{ width: '100%' }}
        >
          {errorMessage || successMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default ClientUserList;
